import React, { useCallback } from "react";
import { makeStyles, useId } from "@fluentui/react-components";
import { useDevicesContext } from "../context/DevicesContext";
import { ListOption } from "../data/ListOption";
import { useDeviceDrawerStore } from "../store/DeviceDrawerStore";
import { Device } from "../data/Device";
import useGetOptionHook from "../../Categories/hooks/useGetOptionHook";

const useStyles = makeStyles({
    drawerBody: {
        overflow: "hidden"
    },
    drawerFooter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "10px",
    },
    inputWrapper: {
        paddingBottom: "15px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        maxWidth: "400px",
        "> div": { display: "flex", flexDirection: "column", gap: "2px" },
    },
});

export type CategoryStateProps = {
    id: string;
    value: number;
    setValue: (value: number) => void;
    list: ListOption | undefined;
};

export type InputStateProps<Type> = {
    id: string;
    value?: Type;
    setValue: (value: Type) => void;
}

function useCategoryState(categoryId: number, listOptions: ListOption[] | undefined, initialValue: number) {
    const id = useId(`category-${categoryId}`);
    const [value, setValue] = React.useState<number>(initialValue);

    const list = React.useMemo(() => {
        return listOptions?.find(o => o.categoryId === categoryId)
    }, [listOptions, categoryId]);
    return { id, value, setValue, list } as CategoryStateProps;
}

function useInputState<Type>(name: string, initialValue: Type) {
    const id = useId(name);
    const [value, setValue] = React.useState<Type>(initialValue);
    return { id, value, setValue } as InputStateProps<Type>;
}

export default function useDeviceFormHook() {
    const styles = useStyles();
    const { listOptions } = useDevicesContext();
    const { isEdit, data } = useDeviceDrawerStore();
    const { options } = useGetOptionHook(2); // Assuming 2 is the ID for Device Type
    // Utility to get default option by name (case-insensitive, spaces ignored)
    const getDefaultOptionByName = useCallback((
        state: CategoryStateProps,
        categoryId: number,
        optionName: string
    ) => {
        const list = listOptions?.find(o => o.categoryId === categoryId);
        const normalized = optionName.replaceAll(" ", "").toLocaleLowerCase();
        const optionId = list?.options?.find(
            o => o.name.replaceAll(" ", "").toLocaleLowerCase() === normalized
        )?.id;

        if (optionId) state.setValue(optionId);
    }, [listOptions]);

    const getLabelPrefix = (optionId: number) => {
        const option = options?.find(opt => opt.id === optionId);
        switch (option?.name.toLocaleLowerCase()) {
            case "laptop":
                return "IDL-LT-";
            case "keyboard":
                return "IDL-KB-";
            case "mouse":
                return "IDL-MS-";
            case "headset":
                return "IDL-HS-";
            case "monitor":
                return "IDL-MO-";
            case "webcam":
                return "IDL-WE-";
            case "hub":
                return "IDL-HUB-";
            case "mobile":
                return "IDL-MP-";
            case "network adapter":
                return "IDL-NA-";
            case "switch":
                return "IDL-SW-";
            case "camera ip":
                return "IDL-CM-";
            case "camera recorder":
                return "IDL-RE-";
            case "finger access control":
                return "IDL-FI-";
            case "firewall":
                return "IDL-FW-";
            case "hanet":
                return "IDL-HA-";
            case "hub(usb,hdml,lan)":
                return "IDL-HUB-";
            case "nas":
                return "IDL-NAS-";
            case "network cable":
                return "IDL-CABLE-";
            case "network plier":
                return "IDL-FLIER-";
            case "printer":
                return "IDL-PT-";
            case "projector":
                return "IDL-MC-";
            case "router":
                return "IDL-RT-";
            case "server":
                return "IDL-SV-";
            case "television":
                return "IDL-TV-";
            case "ups":
                return "IDL-UPS-";
            case "usb media":
                return "IDL-USB-";
            case "wi-fi extender":
                return "IDL-EXT-";
            case "wi-fi router":
                return "IDL-RT-";
            default:
                return "IDL-";
        }
    };

    const defaultWarrantyDate = React.useMemo(() => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        return date;
    }, []);

    const getDefaultValue = React.useCallback(<Type>(key: keyof Device, fallback: Type): Type => {
        return isEdit && data ? (data[key] as Type) : fallback;
    }, [isEdit, data]);

    const labelState = useInputState("label", getDefaultValue("label", ""));
    const serialNumberState = useInputState("serialNumber", getDefaultValue("serialNumber", ""));
    const cpuState = useInputState("cpu", "");
    const memoryState = useInputState("memory", "");
    const diskState = useInputState("disk", getDefaultValue("disk", ""));
    const priceState = useInputState("price", getDefaultValue("price", 0));
    const purchaseDateState = useInputState("purchaseDate", new Date(getDefaultValue("purchaseDate", new Date())));
    const isWarrantyState = useInputState("isWarranty", getDefaultValue("isWarranty", true));
    const laptopBagState = useInputState("laptopBag", getDefaultValue("laptopBag", true));
    const warrantyDateState = useInputState("warrantyDate", new Date(getDefaultValue("warrantyDate", defaultWarrantyDate)));

    const ownerState = useCategoryState(1, listOptions, getDefaultValue("owner", 0));
    const typeState = useCategoryState(2, listOptions, getDefaultValue("type", 0));
    const vendorState = useCategoryState(3, listOptions, getDefaultValue("vendor", 0));
    const manufacturerState = useCategoryState(4, listOptions, getDefaultValue("manufacturer", 0));
    const modelState = useCategoryState(5, listOptions, getDefaultValue("model", 0));
    const integrityState = useCategoryState(6, listOptions, getDefaultValue("integrity", 0));
    const confidentialityState = useCategoryState(7, listOptions, getDefaultValue("confidentiality", 0));
    const availabilityState = useCategoryState(8, listOptions, getDefaultValue("availability", 0));
    const statusState = useCategoryState(9, listOptions, getDefaultValue("status", 0));
    const locationState = useCategoryState(10, listOptions, getDefaultValue("location", 0));

    React.useEffect(() => {
        if (!isEdit) {
            getDefaultOptionByName(ownerState, 1, "iDealogic");
            getDefaultOptionByName(integrityState, 6, "High");
            getDefaultOptionByName(confidentialityState, 7, "High");
            getDefaultOptionByName(availabilityState, 8, "High");
            getDefaultOptionByName(statusState, 9, "Return To IT");
        }
    }, [isEdit, listOptions, ownerState, integrityState, confidentialityState, availabilityState, statusState, getDefaultOptionByName]);

    return {
        styles,
        labelState,
        serialNumberState,
        cpuState,
        memoryState,
        diskState,
        priceState,
        purchaseDateState,
        isWarrantyState,
        warrantyDateState,
        ownerState,
        typeState,
        vendorState,
        manufacturerState,
        modelState,
        integrityState,
        confidentialityState,
        availabilityState,
        statusState,
        laptopBagState,
        locationState,
        getLabelPrefix,
    };
}
